import React, { useEffect, useState } from "react";
import { CharacterListItem, CampaignListItem, CharacterListRequest } from "../../types/types";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../services/apiClient";
import { getTotalPages } from "../../util/util";
import {
  Button,
  DataTable,
  FilterBar,
  LoadingState,
  EmptyState,
  ErrorBoundary,
  ConfirmationDialog,
  useToast,
} from "../../components/ui";
import type { Column } from "../../components/ui/DataTable";
import type { FilterConfig } from "../../components/ui";
import { useFilters } from "../../hooks";
import { CampaignService } from "../../services/campaignService";
import { CharacterService } from "../../services/characterService";
import { CharacterCard, CharacterAction } from "../../components/business";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTable,
  faThLarge,
  faEdit,
  faTrash,
  faDice,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { PageHeader, PageContainer, ContentSection } from "../../components/layout";

interface CharacterFilters {
  name: string;
  campaignId: string;
}

type ViewMode = "table" | "cards";

const CharactersList: React.FC = () => {
  const pageSize = 10;
  const toast = useToast();
  const [characters, setCharacters] = useState<CharacterListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableCampaigns, setAvailableCampaigns] = useState<CampaignListItem[]>([]);
  const [campaignsMap, setCampaignsMap] = useState<Map<number, string>>(new Map());
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const navigate = useNavigate();

  // Filter configuration - campaigns options will be populated dynamically
  const getFilterConfigs = (): FilterConfig[] => [
    {
      key: "name",
      type: "text",
      label: "Hero Name",
      placeholder: "Search by name",
      className: "min-w-[200px]",
    },
    {
      key: "campaignId",
      type: "select",
      label: "Chronicle",
      placeholder: "All chronicles",
      className: "min-w-[200px]",
      options: [
        { value: "", label: "All chronicles" },
        ...availableCampaigns.map((campaign) => ({
          value: campaign.id.toString(),
          label: campaign.name,
        })),
      ],
    },
  ];

  const { filters, updateFilter, resetFilters } = useFilters<CharacterFilters>({
    initialValues: { name: "", campaignId: "" },
    onFiltersChange: () => setCurrentPage(0), // Reset to first page when filters change
  });

  const handleEdit = (id: number) => {
    navigate(`/protected/characters/${id}`);
  };

  const handleCreate = () => {
    navigate(`/protected/characters/create`);
  };

  // Define table columns
  const columns: Column<CharacterListItem>[] = [
    {
      key: "name",
      header: "Name",
      render: (character) => <span className="text-zinc-200 font-medium">{character.name}</span>,
    },
    {
      key: "description",
      header: "Description",
      render: (character) => (
        <span className="truncate max-w-xs block">{character.description || "-"}</span>
      ),
    },
    {
      key: "campaignId",
      header: "Campaign",
      render: (character) => {
        if (!character.campaignId) return "-";
        return campaignsMap.get(character.campaignId) || `ID: ${character.campaignId}`;
      },
    },
    {
      key: "createdAt",
      header: "Created",
      render: (character) => new Date(character.createdAt).toLocaleDateString(),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      render: (character) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => handleEdit(character.id)}
            className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-amber-400 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => navigate(`/protected/characters/${character.id}#dice-templates`)}
            className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 transition-colors"
          >
            Fate Formulas
          </button>
          <button
            onClick={() => handleDuplicate(character.id)}
            className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 transition-colors"
          >
            Duplicate
          </button>
          <button
            onClick={() => handleDeleteConfirm(character.id)}
            className="px-3 py-1.5 text-xs bg-red-900/40 border border-red-800/60 text-red-400 hover:bg-red-900/60 transition-colors"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const fetchCharacters = async (
    page: number,
    pageSize: number,
    currentFilters: CharacterFilters
  ) => {
    try {
      setLoading(true);
      setError(null);

      const requestBody: CharacterListRequest = {
        name: currentFilters.name || "",
        page: page,
        pageSize: pageSize,
      };

      // Only include campaignId if it's not empty - send as string
      if (currentFilters.campaignId && currentFilters.campaignId !== "") {
        requestBody.campaignId = currentFilters.campaignId;
      }

      const data = await CharacterService.getCharacterList(requestBody);
      setCharacters(data.characters || []);
      setTotal(data.total);
      setTotalPages(getTotalPages(data.total, pageSize));
    } catch (error) {
      console.error("Error fetching heroes:", error);
      setError("Failed to load heroes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = (id: number) => {
    setConfirmDeleteId(id);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await apiFetch(`/api/protected/characters/character/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        // Refresh the list
        await fetchCharacters(currentPage, pageSize, filters);
        toast.showToast({
          type: "success",
          message: "Hero deleted successfully",
        });
      } else {
        toast.showToast({
          type: "error",
          message: "Failed to delete hero. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error deleting hero:", error);
      toast.showToast({
        type: "error",
        message: "Failed to delete hero. Please try again.",
      });
    }

    // Clear the confirmation dialog
    setConfirmDeleteId(null);
  };

  const handleDuplicate = async (id: number) => {
    try {
      await CharacterService.duplicateCharacter(id);
      await fetchCharacters(currentPage, pageSize, filters);
      toast.showToast({ type: "success", message: "Hero duplicated" });
    } catch (error) {
      console.error("Error duplicating hero:", error);
      toast.showToast({ type: "error", message: "Failed to duplicate hero. Please try again." });
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchCharacters(newPage, pageSize, filters);
  };

  // Fetch available campaigns for the filter dropdown
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const campaigns = await CampaignService.getAvailableCampaigns();
        setAvailableCampaigns(campaigns);

        // Create a map for quick campaign name lookups
        const map = new Map<number, string>();
        campaigns.forEach((campaign) => {
          map.set(campaign.id, campaign.name);
        });
        setCampaignsMap(map);
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
        setAvailableCampaigns([]);
        setCampaignsMap(new Map());
      }
    };

    fetchCampaigns();
  }, []);

  useEffect(() => {
    fetchCharacters(currentPage, pageSize, filters);
  }, [currentPage, filters]);

  return (
    <ErrorBoundary errorMessage="There was an error loading your heroes. Please try refreshing the page.">
      <PageContainer maxWidth="full">
        {/* Header */}
        <PageHeader
          title="Heroes"
          actions={
            <div className="flex items-center space-x-4">
              {/* View Toggle */}
              <div className="flex items-center space-x-2">
                <Button
                  variant={viewMode === "table" ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => setViewMode("table")}
                  className="p-2"
                  title="Table View"
                >
                  <FontAwesomeIcon icon={faTable} />
                </Button>
                <Button
                  variant={viewMode === "cards" ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => setViewMode("cards")}
                  className="p-2"
                  title="Card View"
                >
                  <FontAwesomeIcon icon={faThLarge} />
                </Button>
              </div>

              <Button onClick={handleCreate} variant="primary" size="md">
                Create Hero
              </Button>
            </div>
          }
        />

        {error && (
          <EmptyState
            title="Error"
            description={error}
            icon="error"
            size="md"
            action={
              <Button
                onClick={() => fetchCharacters(currentPage, pageSize, filters)}
                variant="primary"
                size="md"
              >
                Try Again
              </Button>
            }
          />
        )}

        {/* Filters */}
        <ContentSection variant="transparent">
          <FilterBar
            filters={getFilterConfigs()}
            values={filters}
            onChange={updateFilter}
            onReset={resetFilters}
            layout="horizontal"
          />
        </ContentSection>

        {/* Content */}
        <ContentSection variant={viewMode === "table" ? "card" : "transparent"}>
          {viewMode === "table" ? (
            <DataTable
              data={characters}
              columns={columns}
              loading={loading}
              emptyMessage="No heroes found."
              emptyActionLabel="Create Your First Hero"
              onEmptyAction={handleCreate}
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          ) : (
            <div className="space-y-4">
              {loading ? (
                <LoadingState message="Loading heroes..." size="lg" variant="spinner" />
              ) : characters.length === 0 ? (
                <EmptyState
                  title="No heroes Found"
                  description="You haven't created any heroes yet."
                  size="lg"
                  action={
                    <Button onClick={handleCreate} variant="primary" size="md">
                      Create Your First Hero
                    </Button>
                  }
                />
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {characters.map((character) => {
                      const cardActions: CharacterAction[] = [
                        {
                          label: "Edit",
                          icon: faEdit,
                          onClick: () => handleEdit(character.id),
                          variant: "secondary",
                        },
                        {
                          label: "Fate Formulas",
                          icon: faDice,
                          onClick: () =>
                            navigate(`/protected/characters/${character.id}#dice-templates`),
                          variant: "primary",
                        },
                        {
                          label: "Duplicate",
                          icon: faCopy,
                          onClick: () => handleDuplicate(character.id),
                          variant: "secondary",
                        },
                        {
                          label: "Delete",
                          icon: faTrash,
                          onClick: () => handleDeleteConfirm(character.id),
                          variant: "danger",
                        },
                      ];
                      return (
                        <CharacterCard
                          key={character.id}
                          character={character}
                          variant="default"
                          actions={cardActions}
                          onClick={() => handleEdit(character.id)}
                          campaignName={
                            character.campaignId
                              ? campaignsMap.get(character.campaignId)
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>

                  {/* Pagination for card view */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2 mt-6">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                      >
                        Previous
                      </Button>
                      <span className="text-zinc-400">
                        Page {currentPage + 1} of {totalPages}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages - 1}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </ContentSection>
      </PageContainer>

      {/* Confirmation Dialog for Character Deletion */}
      <ConfirmationDialog
        isOpen={confirmDeleteId !== null}
        title="Delete Hero"
        message="Are you sure you want to delete this hero? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => confirmDeleteId && handleDelete(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
        variant="danger"
      />
    </ErrorBoundary>
  );
};

export default CharactersList;
